
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { CalendarDays, LogOut, Settings, Menu, Clock, CalendarPlus, MessageCircle, Copy, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import Logo from './Logo';
import { api, Store, getTrialStatus, Staff, isLineEnabled } from '../api';
import { useAuth } from '../contexts/AuthContext';
import Modal from './ui/Modal';
import { useToast } from '../contexts/ToastContext';

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  
  const [userProfile, setUserProfile] = useState<Staff | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false);
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  
  const [isLineModalOpen, setIsLineModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  useEffect(() => {
      if (!currentUser) return;
      const unsubscribe = api.listenToStaffProfile(currentUser.uid, (staff) => {
          if (staff) setUserProfile(staff);
      });
      return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const loadData = async () => {
        try {
            const stores = await api.getStores();
            if (stores.length > 0) setCurrentStore(stores[0]);
        } catch (e) {
            console.error('Failed to fetch stores in layout', e);
        }
    };
    loadData();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await api.signOut();
      showToast('ログアウトしました。', 'success');
    } catch (e) {
      console.error('ログアウトエラー:', e);
    }
    navigate('/login');
  };

  const handleOpenLineModal = async () => {
      if (!userProfile) return;
      setIsLineModalOpen(true);
      if (!inviteUrl) {
          setIsGenerating(true);
          try {
              const url = await api.generateInviteToken(userProfile.id);
              setInviteUrl(url);
          } catch (e) {
              showToast('URLの生成に失敗しました。', 'error');
          } finally {
              setIsGenerating(false);
          }
      }
  };

  const trialInfo = useMemo(() => {
      if (!currentStore) return null;
      return getTrialStatus(currentStore);
  }, [currentStore]);

  const remainingDays = trialInfo?.daysRemaining ?? 60;
  const isTrial = !currentStore?.stripeSubscriptionId;
  const isManager = userProfile?.role === 'system_admin' || userProfile?.role === 'store_manager';

  // 勤怠管理機能が表示されるかどうかの判定 (ビジネスプラン or トライアル中)
  const isAttendanceVisible = useMemo(() => {
    if (!currentStore) return false;
    const trial = getTrialStatus(currentStore);
    const isTrialActive = !currentStore.stripeSubscriptionId && !trial.isExpired;
    return currentStore.plan === 'business' || isTrialActive;
  }, [currentStore]);

  const NavItem = ({ path, icon: Icon, label, onClick, exact = false }: { path?: string, icon: React.ElementType, label: string, onClick?: () => void, exact?: boolean }) => {
      const active = path ? (exact ? location.pathname === path : location.pathname.startsWith(path)) : false;
      const handleClick = () => {
          if (onClick) onClick();
          else if (path) navigate(path);
          setIsSidebarOpen(false);
      };

      return (
          <div
            onClick={handleClick}
            className={`group flex items-center px-4 py-3.5 mx-3 rounded-[4px] transition-all cursor-pointer relative ${active ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100'}`}
          >
             <Icon className={`w-5 h-5 ${isDesktopCollapsed ? 'mx-auto' : 'mr-3'}`} />
             {!isDesktopCollapsed && <span className="text-sm font-bold tracking-wide">{label}</span>}
          </div>
      );
  };

  const isLineConnected = userProfile?.lineStatus === 'connected';
  const lineEnabled = isLineEnabled(currentStore);

  return (
    <div className="h-screen bg-[#F8FAFC] flex flex-col lg:flex-row font-sans text-slate-700 relative overflow-hidden">
      {/* モバイルヘッダー */}
      <header className={`lg:hidden h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 shrink-0 z-50 shadow-sm`}>
        <Logo size="sm" />
        <button onClick={() => setIsSidebarOpen(true)} className="p-2 text-slate-500 hover:bg-slate-50 rounded-full transition-colors"><Menu size={24} /></button>
      </header>

      {isSidebarOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-[2px] z-[60] lg:hidden animate-in fade-in duration-300" onClick={() => setIsSidebarOpen(false)} />
      )}

      <aside className={`bg-white h-full fixed inset-y-0 left-0 z-[70] flex flex-col border-r border-slate-200 transition-transform duration-300 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:relative lg:z-40 ${isDesktopCollapsed ? 'w-20' : 'w-72'}`}>
        <div className="p-8 hidden lg:flex items-center justify-between">
          <Logo size={isDesktopCollapsed ? "sm" : "md"} />
        </div>

        <nav className="flex-1 py-4 space-y-1 overflow-y-auto no-scrollbar">
          <NavItem path="/admin/shifts" icon={CalendarDays} label="シフト確認" />
          <NavItem path="/admin/shift-creation" icon={CalendarPlus} label="シフト作成" exact />
          {/* 勤怠管理は権限とプランに応じて表示 */}
          {isAttendanceVisible && (
            <NavItem path="/admin/attendance" icon={Clock} label="勤怠管理" />
          )}
          <NavItem path="/admin/settings" icon={Settings} label="設定・プラン" />
          
          {/* ログアウト動線をメニュー末尾に統合 */}
          <div className="pt-4 mt-4 border-t border-slate-100">
            <NavItem icon={LogOut} label="ログアウト" onClick={handleLogout} />
          </div>
        </nav>

        <div className="p-4 mt-auto bg-slate-50/50 border-t shrink-0">
            <div className="flex flex-col gap-3">
                {/* プラン表示カード */}
                {isManager && isTrial && (
                    <div className="p-5 bg-[#1e293b] rounded-xl shadow-lg">
                        <p className="text-[10px] font-black text-slate-400 mb-3 tracking-widest uppercase opacity-80">現在のプラン</p>
                        <div className="flex items-center justify-between mb-2.5">
                            <span className="text-[11px] font-black text-white/90">無料トライアル 残り</span>
                            <div className="flex items-baseline gap-1 text-white">
                                <span className="text-3xl font-black tracking-widest leading-none">{remainingDays}</span>
                                <span className="text-[10px] font-black ml-1">日</span>
                            </div>
                        </div>
                        <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-[#10b981] rounded-full transition-all duration-1000 ease-out" 
                                style={{ width: `${Math.min(100, (remainingDays / 60) * 100)}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* LINE連携ステータスカード（LINEを使う運用の店舗のみ） */}
                {isManager && lineEnabled && (
                    <div 
                        className={`p-4 rounded-xl border transition-all cursor-pointer group relative ${isLineConnected ? 'bg-white border-slate-200' : 'bg-orange-50/50 border-orange-200 shadow-sm'}`}
                        onClick={handleOpenLineModal}
                    >
                        <div className="flex items-center gap-2 mb-2">
                            {isLineConnected ? <ShieldCheck size={16} className="text-green-600" /> : <ShieldAlert size={16} className="text-orange-500" />}
                            <span className={`text-[10px] font-black uppercase tracking-wider ${isLineConnected ? 'text-slate-400' : 'text-orange-600'}`}>
                                {isLineConnected ? '管理者LINE 連携中' : '管理者LINE 未連携'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="font-bold text-sm text-slate-700 truncate pr-8">
                                {userProfile ? `${userProfile.lastName} ${userProfile.firstName}` : '...'}
                            </p>
                            <span className="text-[10px] text-blue-600 font-bold border-b border-blue-200 hover:text-blue-700 transition-colors">設定</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
      </aside>

      <main className="flex-1 h-full overflow-y-auto relative flex flex-col">
        <div className="max-w-7xl w-full mx-auto p-4 lg:p-8 flex-1">
          {children || <Outlet />}
        </div>
      </main>

      <Modal isOpen={isLineModalOpen} onClose={() => setIsLineModalOpen(false)} title="管理者用LINEの連携" maxWidth="max-w-md">
          <div className="space-y-6 pt-2">
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <div className="text-xs text-slate-600 font-bold leading-relaxed">
                      <p className="mb-1 text-blue-700 font-black text-sm">【管理者用LINEについて】</p>
                      管理者権限を持つスタッフが、ここからLINEをスキャンすることで、店舗の運営に関わる通知（欠勤連絡や助っ人依頼の回答など）を受け取れるようになります。なお、初回のチュートリアルで連携済みの方は再度の連携は必要ありません。
                  </div>
              </div>

              <div className="flex flex-col items-center justify-center p-6 bg-white border border-slate-200 rounded-2xl shadow-inner">
                  {isGenerating ? (
                      <div className="h-40 flex flex-col items-center justify-center gap-3">
                          <Loader2 className="animate-spin text-blue-600" size={32} />
                          <span className="text-xs text-slate-400 font-bold">連携URLを生成中...</span>
                      </div>
                  ) : inviteUrl ? (
                      <div className="flex flex-col items-center gap-6 w-full">
                          <div className="p-2 bg-white border-4 border-slate-100 rounded-xl shadow-lg">
                              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(inviteUrl)}`} alt="LINE招待QR" className="w-40 h-40" />
                          </div>
                          <div className="w-full space-y-3">
                              <button 
                                  onClick={() => { navigator.clipboard.writeText(inviteUrl); showToast('リンクをコピーしました。'); }}
                                  className="w-full bg-slate-800 text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-black transition-all active:scale-95"
                              >
                                  <Copy size={16} /> 連携URLをコピー
                              </button>
                              <a href={inviteUrl} target="_blank" rel="noopener noreferrer" className="w-full bg-[#06C755] text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:brightness-105 transition-all active:scale-95">
                                  <MessageCircle size={18} /> LINEで開く
                              </a>
                          </div>
                      </div>
                  ) : null}
              </div>
          </div>
      </Modal>
    </div>
  );
};

export default DashboardLayout;
